import { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Send, Loader2, Filter, ListFilter, Users, AlertTriangle, CheckCircle2, ShieldAlert,
  Download, Printer, Copy,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ConditionsBuilder from "@/components/ConditionsBuilder";

/**
 * Página "Disparo Manual com Filtros Inteligentes".
 *
 * Permite ao usuário:
 *  1. Montar o MESMO tipo de filtro disponível nas Automações (ConditionsBuilder).
 *  2. Visualizar quem casa com o filtro (chama /api/automations/preview — dry-run).
 *  3. Marcar/desmarcar destinatários específicos.
 *  4. Escrever mensagem com variáveis {nome}, {primeiro_nome}.
 *  5. Disparar via /api/whatsapp/campaigns/bulk-send (rate-limit ativo + grava em
 *     campaign_sends, então o Histórico/Audit reflete o envio).
 *
 * Workflow obrigatório:
 *  filtro → pré-visualizar → revisar lista → escrever mensagem → confirmar 2× → enviar
 *
 * NÃO usa automação. Tudo é manual e rastreado.
 */
export default function DisparoManual() {
  const { apiCall } = useAuth();

  // Estado do filtro
  const [contextKind] = useState("client_filter");  // fallback quando não detectamos appt/invoice

  // 🎯 fix jul/2026: detecta contexto automaticamente pelas condições.
  // Se algum filtro usa "appointment.*" → context "appointment"; "invoice.*" → "invoice".
  // Senão continua "client_filter". Antes era fixo "client_filter" → filtros de agendamento nunca casavam.
  const detectContextKind = (conds) => {
    const scan = (nodes) => {
      for (const n of nodes || []) {
        if (n?.logic && n?.conditions) {
          const r = scan(n.conditions);
          if (r) return r;
        } else if (typeof n?.field === "string") {
          if (n.field.startsWith("appointment.")) return "appointment";
          if (n.field.startsWith("invoice.")) return "invoice";
        }
      }
      return null;
    };
    return scan(conds) || "client_filter";
  };
  const [conditions, setConditions] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);  // {matched, samples}
  const [recipients, setRecipients] = useState([]);  // lista completa de matches
  const [selected, setSelected] = useState({});      // {client_id: true}

  // Estado da mensagem
  const [message, setMessage] = useState("Oi {primeiro_nome}! 🌿\n\n");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  const runPreview = async () => {
    if (!conditions.length) {
      toast.warning("Adicione pelo menos um filtro");
      return;
    }
    setPreviewLoading(true);
    setPreviewResult(null);
    setRecipients([]);
    setSelected({});
    try {
      // 1) Pega o count + amostras (dry-run) — usa context detectado
      const detected = detectContextKind(conditions);
      const r = await apiCall("post", "/automations/preview", {
        context_kind: detected,
        conditions,
        limit_samples: 2000,
      });
      const { matched = 0, samples = [] } = r.data || {};
      setPreviewResult({ matched, samples_count: samples.length });
      // 2) Os "samples" do preview já vêm com nome, phone, funnel_status, days_since_last_haircut
      // Marcamos tudo selecionado por padrão, mas o user pode desmarcar
      setRecipients(samples);
      const initialSel = {};
      samples.forEach((s) => {
        if (s.phone) initialSel[s.id] = true;
      });
      setSelected(initialSel);
      if (matched > samples.length) {
        toast.warning(
          `Foram encontrados ${matched} clientes mas só posso mostrar ${samples.length} aqui. Refine o filtro pra trabalhar com a lista completa.`
        );
      } else {
        toast.success(`${matched} cliente(s) encontrado(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao buscar clientes");
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleAll = (on) => {
    const next = {};
    recipients.forEach((r) => {
      if (r.phone) next[r.id] = on;
    });
    setSelected(next);
  };

  // ======= AÇÕES SECUNDÁRIAS (não disparam mensagem) =======
  const exportCSV = () => {
    const rows = recipients.filter((r) => selected[r.id]);
    if (!rows.length) {
      toast.warning("Selecione pelo menos 1 cliente");
      return;
    }
    const header = ["Nome", "Telefone", "Funil", "Dias sem corte"];
    const lines = [header.join(";")];
    rows.forEach((r) => {
      lines.push([
        `"${(r.name || "").replace(/"/g, '""')}"`,
        `"${r.phone || ""}"`,
        r.funnel_status || "",
        r.days_since_last_haircut ?? "",
      ].join(";"));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lista-disparo-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`CSV gerado com ${rows.length} cliente(s)`);
  };

  const copyPhones = () => {
    const rows = recipients.filter((r) => selected[r.id] && r.phone);
    if (!rows.length) { toast.warning("Selecione pelo menos 1 cliente"); return; }
    const text = rows.map((r) => `${r.name}: ${r.phone}`).join("\n");
    navigator.clipboard.writeText(text);
    toast.success(`${rows.length} contato(s) copiados pra área de transferência`);
  };

  const printList = () => {
    const rows = recipients.filter((r) => selected[r.id]);
    if (!rows.length) { toast.warning("Selecione pelo menos 1 cliente"); return; }
    const win = window.open("", "_blank");
    if (!win) { toast.error("Bloqueado pelo navegador. Permita popups."); return; }
    win.document.write(`
      <html><head><title>Lista de Disparo - ${new Date().toLocaleDateString("pt-BR")}</title>
      <style>
        body { font-family: -apple-system, sans-serif; padding: 20px; color: #1a1a1a; }
        h1 { font-size: 18px; margin-bottom: 4px; }
        .meta { font-size: 11px; color: #666; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
        th { background: #f0f0f0; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; }
        tr:nth-child(even) { background: #fafafa; }
        @media print { body { padding: 0; } }
      </style></head><body>
      <h1>Lista de Disparo Manual</h1>
      <div class="meta">${rows.length} clientes · Gerado em ${new Date().toLocaleString("pt-BR")}</div>
      <table><thead><tr><th>#</th><th>Nome</th><th>Telefone</th><th>Funil</th><th>Dias s/ corte</th></tr></thead><tbody>
      ${rows.map((r, i) => `<tr><td>${i + 1}</td><td>${r.name || "—"}</td><td>${r.phone || "—"}</td><td>${r.funnel_status || "—"}</td><td>${r.days_since_last_haircut ?? "—"}</td></tr>`).join("")}
      </tbody></table>
      <script>window.onload = () => window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (selectedCount === 0) {
      toast.error("Nenhum destinatário selecionado");
      return;
    }
    if (!message.trim()) {
      toast.error("Mensagem vazia");
      return;
    }
    // Confirmação final EXTRA via window.confirm
    if (!window.confirm(
      `🚨 Confirmação final\n\nVai enviar uma mensagem para ${selectedCount} cliente(s) AGORA.\n\nNão tem volta. Continuar?`
    )) return;

    setSending(true);
    setLastResult(null);
    try {
      // Monta payload pro /whatsapp/campaigns/bulk-send
      const client_ids = recipients
        .filter((r) => selected[r.id] && r.phone)
        .map((r) => r.id);
      const r = await apiCall("post", "/whatsapp/campaigns/bulk-send", {
        client_ids,
        message: message,
      });
      setLastResult(r.data);
      toast.success(`Disparo iniciado: ${client_ids.length} cliente(s) na fila.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao disparar");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="disparo-manual-page">
      {/* 🎯 BANNER — mando o usuário para a página nova */}
      <Card className="p-4 border-2 border-primary bg-gradient-to-r from-primary/10 to-primary/5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="text-3xl">🎯</div>
            <div>
              <div className="font-medium text-base">Este é o modo AVANÇADO/legado do Disparo Manual</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Para o fluxo recomendado — <b>7 segmentos prontos, prévia com telefone mascarado, dedupe automático e mensagens pré-configuradas</b> — use a página Segmentos.
              </div>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => { window.location.href = "/segmentos"; }}
            data-testid="go-to-segments-btn"
            className="bg-primary hover:bg-primary/90"
          >
            Ir para Segmentos →
          </Button>
        </div>
      </Card>
      <Card className="p-4 border border-amber-300 bg-amber-50">
        <div className="flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-700 mt-0.5" strokeWidth={1.5} />
          <div className="text-xs">
            <div className="font-medium text-amber-900 mb-1">Disparo manual com rastreamento</div>
            <p className="text-amber-800 leading-relaxed">
              Esta página usa os mesmos filtros das automações, mas é <b>100% manual</b>: você monta o filtro,
              revisa a lista, marca/desmarca, escreve a mensagem e confirma o envio. Todos os disparos respeitam
              o rate-limit (máx 80/h + jitter 8–30s) e ficam registrados em <code>campaign_sends</code>, então
              aparecem no Histórico normalmente. Use enquanto a Automação está em lockdown.
            </p>
          </div>
        </div>
      </Card>

      {/* PASSO 1: Filtro */}
      <Card className="p-4 border border-border bg-card shadow-soft" data-testid="filter-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-primary" strokeWidth={1.5} />
            <h3 className="text-sm font-medium">1. Monte o filtro</h3>
          </div>
          <Badge variant="outline" className="text-[10px]">contexto: cliente</Badge>
        </div>
        <div className="mb-3 p-2 rounded bg-blue-50 border border-blue-200">
          <div className="text-[10px] uppercase tracking-widest text-blue-900 mb-1">Exemplos rápidos de filtro</div>
          <ul className="text-[11px] text-blue-900 space-y-1 ml-3 list-disc">
            <li><b>Sem corte 90+ dias</b>: <code>client.days_since_last_haircut &gt;= 90</code></li>
            <li><b>Sem luzes 60+ dias</b>: <code>client.days_since_last_highlights &gt;= 60</code></li>
            <li><b>Último serviço foi corte</b>: <code>{`client.last_service_name contains "corte"`}</code></li>
            <li><b>Cliente VIP</b>: <code>client.total_visits &gt;= 5</code> AND <code>client.avg_ticket &gt;= 150</code></li>
            <li><b>Aniversariantes do mês</b>: <code>client.birthday_month == [mês]</code></li>
            <li><b>Tem fiado em aberto</b>: <code>client.has_open_invoice == true</code></li>
            <li><b>Cadastro incompleto</b>: <code>client.has_incomplete_data == true</code></li>
            <li><b>Status do funil</b>: <code>client.funnel_status in [verde, azul]</code></li>
          </ul>
        </div>
        <ConditionsBuilder
          value={conditions}
          onChange={setConditions}
        />
        <div className="mt-3 flex gap-2">
          <Button
            onClick={runPreview}
            disabled={previewLoading || !conditions.length}
            data-testid="btn-preview"
            className="bg-primary"
          >
            {previewLoading ? (
              <><Loader2 className="h-3 w-3 mr-1 animate-spin" strokeWidth={1.5} />Buscando…</>
            ) : (
              <><ListFilter className="h-3 w-3 mr-1" strokeWidth={1.5} />Pré-visualizar lista</>
            )}
          </Button>
          {previewResult && (
            <div className="flex items-center text-xs text-muted-foreground">
              {previewResult.matched} cliente(s) casaram · {previewResult.samples_count} carregados
            </div>
          )}
        </div>
      </Card>

      {/* PASSO 2: Lista de destinatários */}
      {recipients.length > 0 && (
        <Card className="p-4 border border-border bg-card shadow-soft" data-testid="recipients-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" strokeWidth={1.5} />
              <h3 className="text-sm font-medium">
                2. Revise destinatários — <span className="text-emerald-700">{selectedCount} selecionados</span>
              </h3>
            </div>
            <div className="flex gap-1 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => toggleAll(true)} data-testid="btn-select-all" className="text-xs h-7">
                Selecionar todos
              </Button>
              <Button variant="outline" size="sm" onClick={() => toggleAll(false)} data-testid="btn-deselect-all" className="text-xs h-7">
                Desmarcar todos
              </Button>
              <div className="border-l border-border mx-1" />
              <Button variant="outline" size="sm" onClick={copyPhones} data-testid="btn-copy-phones" className="text-xs h-7" title="Copiar nome+telefone dos selecionados">
                <Copy className="h-3 w-3 mr-1" strokeWidth={1.5} />Copiar
              </Button>
              <Button variant="outline" size="sm" onClick={exportCSV} data-testid="btn-export-csv" className="text-xs h-7" title="Baixar como planilha CSV">
                <Download className="h-3 w-3 mr-1" strokeWidth={1.5} />CSV
              </Button>
              <Button variant="outline" size="sm" onClick={printList} data-testid="btn-print" className="text-xs h-7" title="Imprimir a lista">
                <Printer className="h-3 w-3 mr-1" strokeWidth={1.5} />Imprimir
              </Button>
            </div>
          </div>
          <div className="border border-border rounded max-h-[400px] overflow-y-auto bg-background">
            <table className="w-full text-xs">
              <thead className="bg-secondary/40 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-2 py-1 w-8"></th>
                  <th className="px-2 py-1">Cliente</th>
                  <th className="px-2 py-1">Telefone</th>
                  <th className="px-2 py-1">Funil</th>
                  <th className="px-2 py-1">Dias s/ corte</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((r) => (
                  <tr key={r.id} className={`border-t border-border/40 ${!r.phone ? "opacity-50" : ""}`} data-testid={`recipient-${r.id}`}>
                    <td className="px-2 py-1">
                      <Checkbox
                        checked={!!selected[r.id]}
                        disabled={!r.phone}
                        onCheckedChange={(v) => setSelected({ ...selected, [r.id]: !!v })}
                        data-testid={`check-${r.id}`}
                      />
                    </td>
                    <td className="px-2 py-1 max-w-[180px] truncate" title={r.name}>{r.name || "—"}</td>
                    <td className="px-2 py-1">
                      {r.phone || <span className="text-amber-700">sem telefone</span>}
                    </td>
                    <td className="px-2 py-1 text-muted-foreground">{r.funnel_status || "—"}</td>
                    <td className="px-2 py-1 text-muted-foreground">{r.days_since_last_haircut ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* PASSO 3: Mensagem */}
      {recipients.length > 0 && (
        <Card className="p-4 border border-border bg-card shadow-soft" data-testid="message-card">
          <div className="flex items-center gap-2 mb-3">
            <Send className="h-4 w-4 text-primary" strokeWidth={1.5} />
            <h3 className="text-sm font-medium">3. Escreva a mensagem</h3>
          </div>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Digite a mensagem…"
            className="min-h-[140px] text-sm font-mono"
            data-testid="message-input"
          />
          <div className="text-[10px] text-muted-foreground mt-1">
            Variáveis disponíveis: <code>{"{nome}"}</code>, <code>{"{primeiro_nome}"}</code>
          </div>
          <div className="mt-3 p-3 rounded bg-blue-50 border border-blue-200">
            <div className="text-[10px] uppercase tracking-widest text-blue-900 mb-1">Pré-visualização (primeiro selecionado)</div>
            <pre className="text-xs whitespace-pre-wrap font-sans">
              {(() => {
                const firstSel = recipients.find((r) => selected[r.id]);
                if (!firstSel) return "(nenhum selecionado)";
                const nome = firstSel.name || "Cliente";
                return message
                  .replaceAll("{nome}", nome)
                  .replaceAll("{primeiro_nome}", nome.split(" ")[0]);
              })()}
            </pre>
          </div>
        </Card>
      )}

      {/* PASSO 4: Disparar */}
      {recipients.length > 0 && (
        <Card className="p-4 border border-emerald-300 bg-emerald-50/30 shadow-soft" data-testid="action-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">
              Pronto para disparar <span className="font-medium text-emerald-700">{selectedCount}</span> mensagem(ns).
              <div className="text-[11px] text-muted-foreground">
                Ritmo: 80 msg/h máx · jitter 8-30s entre envios · todos gravados em campaign_sends
              </div>
            </div>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={sending || selectedCount === 0 || !message.trim()}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="btn-send"
            >
              {sending ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" strokeWidth={1.5} />Enviando…</>
              ) : (
                <><Send className="h-3 w-3 mr-1" strokeWidth={1.5} />Disparar ({selectedCount})</>
              )}
            </Button>
          </div>
        </Card>
      )}

      {lastResult && (
        <Card className="p-4 border border-border bg-card shadow-soft" data-testid="result-card">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-700" strokeWidth={1.5} />
            <h3 className="text-sm font-medium">Resultado do envio</h3>
          </div>
          <pre className="text-xs bg-secondary/30 p-2 rounded overflow-x-auto">{JSON.stringify(lastResult, null, 2)}</pre>
          <div className="text-xs text-muted-foreground mt-2">
            Acompanhe os envios em <a href="/automacoes" className="underline">Automações → Histórico</a> ou em
            <a href="/campanhas/jobs" className="underline ml-1">Campanhas → Jobs</a>.
          </div>
        </Card>
      )}

      {/* DIALOG: Confirmação inicial */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-700" strokeWidth={1.5} />
              Confirmar disparo manual
            </DialogTitle>
            <DialogDescription>
              Você vai enviar <b>{selectedCount}</b> mensagem(ns) AGORA. Esta ação não tem volta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <div className="p-3 rounded bg-secondary/30 border border-border">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Primeira mensagem (preview)</div>
              <pre className="whitespace-pre-wrap font-sans">
                {(() => {
                  const firstSel = recipients.find((r) => selected[r.id]);
                  if (!firstSel) return "(nenhum selecionado)";
                  const nome = firstSel.name || "Cliente";
                  return message
                    .replaceAll("{nome}", nome)
                    .replaceAll("{primeiro_nome}", nome.split(" ")[0]);
                })()}
              </pre>
              <div className="mt-2 text-muted-foreground">
                Para: <b>{recipients.find((r) => selected[r.id])?.name}</b> (<code>{recipients.find((r) => selected[r.id])?.phone}</code>)
              </div>
            </div>
            <div className="text-amber-800 text-[11px]">
              ⏱ Tempo estimado: ~{Math.ceil((selectedCount * 19) / 60)} min (com jitter médio de 19s).
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="cancel-send">Cancelar</Button>
            <Button onClick={handleConfirm} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="confirm-send">
              Sim, disparar {selectedCount}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
